"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import Image from "next/image"

interface SaveCanvasModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (canvasName: string) => void
  canvasImage: string
}

export function SaveCanvasModal({ isOpen, onClose, onSave, canvasImage }: SaveCanvasModalProps) {
  const [canvasName, setCanvasName] = useState("")

  const handleSave = () => {
    if (canvasName.trim()) {
      onSave(canvasName.trim())
      setCanvasName("")
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Your Canvas</DialogTitle>
          <DialogDescription>
            Give your canvas a name so you can easily find it later in your dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Canvas Preview */}
          <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100">
            <Image src={canvasImage || "/placeholder.svg"} alt="Canvas preview" fill className="object-cover" />
          </div>

          {/* Canvas Name Input */}
          <div className="space-y-2">
            <Label htmlFor="canvas-name">Canvas Name</Label>
            <Input
              id="canvas-name"
              placeholder="e.g., My Kitchen Canvas"
              value={canvasName}
              onChange={(e) => setCanvasName(e.target.value)}
              onKeyPress={handleKeyPress}
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canvasName.trim()}
            className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
          >
            Save Canvas
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
